/**
 * The sign-in page's backdrop — the four programme countries, drawn live (client instruction,
 * 2026-09-08).
 *
 * The entry page's left half used to be IFDC's photograph. The client's line for this round is
 * that it should be *"a map of the operational countries"*, and this is that map: the
 * deployment's default basemap with the level-0 outlines from `/hierarchy/units` over it,
 * fitted to the programme extent, under the same translucent film the photograph had and
 * behind the same copy, word for word. Nothing else on the page changed for it, and no asset
 * was deleted — `gateAssets.BACKDROP` still resolves `backdrop.jpg`; this page simply stops
 * asking for it (see `SignInGate`).
 *
 * ## It is scenery, and every option below says so
 *
 * A reader at the gate has not asked for a map and cannot yet do anything with one. So every
 * handler Leaflet ships is off — drag, wheel, double-click, box, touch, keyboard — there is no
 * zoom control, and the container is `aria-hidden` with `pointer-events:none` over it, so a
 * pointer travelling to the email field never lands on a tile and a screen reader never meets
 * a widget with no purpose. What survives is the attribution: CARTO's and OSM's terms require
 * the credit wherever their tiles are painted, and a licence condition is not chrome.
 *
 * ## The one query, and the reason it costs nothing
 *
 * `queryKeys.units('admin', 0, 'ALL', 'z5')` is **the Overview's own key**, deliberately: the
 * next screen in the session (`OverviewMap`) asks for exactly this payload, and TanStack Query
 * hands it the cached one. The gate therefore moves the request earlier in the session rather
 * than adding one to it.
 *
 * ## If the API is not there, this renders nothing at all
 *
 * Which is the whole of the degradation, and it is deliberate. `#gateArt` carries a soil-green
 * gradient under this layer for exactly the case the photograph had — no file, no network, a
 * cold server — and the film and the copy sit on it unchanged. A basemap with no countries on
 * it would be a *worse* answer than the gradient: it is a map of somewhere, drawn at a zoom
 * nobody chose, with nothing of the programme in it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';

import { fetchUnits, queryKeys, type UnitsFC } from '../../lib/api';
import { useAppStore } from '../../lib/state';
import { resolveBasemap, useBasemaps } from '../map/basemaps';
import { HOME_BOUNDS } from '../map/panes';

/** What `L.geoJSON` will take, without importing a `@types/geojson` we do not have. */
type GeoJsonInput = Parameters<typeof L.geoJSON>[0];

/**
 * The outlines, styled for a panel that is about to be darkened by 44-74 %.
 *
 * The Overview's admin line is a near-black warm stroke, which is right on a light basemap in
 * a bright card and invisible under this page's film. Here the countries are a pale wash with
 * a warm ivory edge — light-on-dark, the inverse of the same idea, so the four shapes still
 * read as shapes once the wash is over them.
 */
const GATE_OUTLINE: L.PathOptions = {
  color: '#FFF3D2',
  weight: 2.4,
  opacity: 0.95,
  fillColor: '#BFE292',
  fillOpacity: 0.46,
};

export function GateMap() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const [map, setMap] = useState<L.Map | null>(null);
  const basemap = useAppStore((s) => s.basemap);
  const basemaps = useBasemaps();

  /**
   * `retry: false`: a gate that spends three backoffs waiting for a decorative payload is a
   * gate that paints its fallback a second too late to be a fallback. One try, then the
   * gradient.
   */
  const { data: outlines } = useQuery<UnitsFC>({
    queryKey: queryKeys.units('admin', 0, 'ALL', 'z5'),
    queryFn: () => fetchUnits('admin', 0, 'ALL', 'z5'),
    retry: false,
    staleTime: 5 * 60_000,
  });

  /** Level-0 features only, and only if there are any: see the module comment. */
  const countries = useMemo(() => {
    const features = outlines?.features ?? [];
    return features.length ? outlines : null;
  }, [outlines]);

  // --- the map, created once the geometry it is for has arrived ---------------
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !countries || mapRef.current) return;
    const m = L.map(el, {
      zoomControl: false,
      // The tile providers' credit, which is a licence condition rather than a control.
      attributionControl: true,
      dragging: false,
      touchZoom: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      inertia: false,
      // The same reason the Overview's map takes it: `fitBounds` rounds *down* to the snap, and
      // a quarter of a zoom level thrown away here is the programme not filling its panel.
      zoomSnap: 0,
    });
    m.attributionControl.setPrefix(false);
    m.fitBounds(HOME_BOUNDS);
    mapRef.current = m;
    setMap(m);
    return () => {
      m.remove();
      mapRef.current = null;
      setMap(null);
    };
  }, [countries]);

  /** The panel's width is a share of the viewport, so the frame moves under the map. */
  useEffect(() => {
    const el = containerRef.current;
    if (!map || !el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => map.invalidateSize({ animate: false }));
    observer.observe(el);
    return () => observer.disconnect();
  }, [map]);

  // --- the deployment's own basemap ------------------------------------------
  useEffect(() => {
    if (!map) return;
    const def = resolveBasemap(basemaps, basemap);
    const layer = L.tileLayer(def.url, { attribution: def.attr, maxZoom: def.maxZoom }).addTo(map);
    layer.bringToBack();
    return () => {
      map.removeLayer(layer);
    };
  }, [map, basemap, basemaps]);

  // --- the four countries, and the fit to them --------------------------------
  useEffect(() => {
    if (!map || !countries) return;
    const layer = L.geoJSON(countries as unknown as GeoJsonInput, {
      style: () => GATE_OUTLINE,
      interactive: false,
      onEachFeature: (feature, shape) => {
        const props = (feature?.properties ?? {}) as { country?: string };
        shape.on('add', () => {
          const el = shape instanceof L.Path ? shape.getElement() : null;
          el?.setAttribute('data-country', String(props.country ?? ''));
          el?.classList.add('gate-country');
        });
      },
    }).addTo(map);
    const bounds = layer.getBounds();
    map.invalidateSize({ animate: false });
    // 6 % of the extent's own size: enough that Burkina's western edge and Nigeria's eastern
    // one are not cut by the panel, and — unlike a fixed pixel inset — the same proportion
    // whether the panel is 693px wide on a 1440 or 340px tall on a phone.
    map.fitBounds(bounds.isValid() ? bounds.pad(0.06) : HOME_BOUNDS, { animate: false });
    return () => {
      map.removeLayer(layer);
    };
  }, [map, countries]);

  if (!countries) return null;

  return (
    <div className="gate-art-map" data-gatemap="" aria-hidden="true">
      <div ref={containerRef} data-gatemapcanvas="" />
    </div>
  );
}

export default GateMap;
